import React, { useState, useEffect } from 'react';
import { jobsAPI, usersAPI } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, History, AlertTriangle } from 'lucide-react';
import { formatCurrency, formatPhoneNumber } from '../utils/currency';

const CreateJob = () => {
  const navigate = useNavigate();
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isHistoric, setIsHistoric] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    location: '',
    assigned_manager: '',
    scheduled_date: '',
    estimated_completion_date: '',
    estimated_cost: 0,
    job_type: 'private',
    client_name: '',
    client_contact: '',
    client_alt_contact: '',
    client_email: '',
    insurer_name: '',
    insurer_contact_person: '',
    insurer_email: '',
    insurer_contact_no: '',
    loss_adjuster_name: '',
    loss_adjuster_contact: '',
    loss_adjuster_contact_no: '',
    loss_adjuster_email: '',
    insurer_reference_no: '',
    insurer_claim_no: '',
    custom_job_code: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const managersRes = await usersAPI.getManagers();
      setManagers(managersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate custom job code for historic jobs
      if (isHistoric && !formData.custom_job_code.trim()) {
        toast.error('Please enter a Job ID for historic jobs');
        setLoading(false);
        return;
      }

      const submitData = {
        ...formData,
        assigned_manager: formData.assigned_manager || null,
        scheduled_date: formData.scheduled_date || null,
        is_historic: isHistoric,
        custom_job_code: isHistoric ? formData.custom_job_code.trim() : null,
      };

      await jobsAPI.create(submitData);
      toast.success(isHistoric ? 'Historic job created successfully!' : 'Job created successfully!');
      navigate('/jobs');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isInsurance = formData.job_type === 'insurance';


  return (
    <div data-testid="create-job-page" className="p-6 max-w-6xl mx-auto">
      <button
        data-testid="back-button"
        onClick={() => navigate('/jobs')}
        className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to Jobs
      </button>

      <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {isHistoric ? 'Create Historic Job' : 'Create New Job'}
          </h1>
          <label className="flex items-center gap-3 cursor-pointer">
            <span className="text-sm text-slate-600">Historic Job</span>
            <div 
              className={`relative w-12 h-6 rounded-full transition-colors ${isHistoric ? 'bg-amber-500' : 'bg-slate-300'}`}
              onClick={() => setIsHistoric(!isHistoric)}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${isHistoric ? 'translate-x-7' : 'translate-x-1'}`} />
            </div>
            <History className={`w-5 h-5 ${isHistoric ? 'text-amber-500' : 'text-slate-400'}`} />
          </label>
        </div>

        {isHistoric && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Historic Job Mode</p>
                <p className="text-sm text-amber-700 mt-1">
                  Use this mode to capture existing/old jobs into the system. You can specify a custom Job ID instead of having one auto-generated.
                </p>
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-amber-800 mb-2">Custom Job ID *</label>
              <input
                type="text"
                name="custom_job_code"
                value={formData.custom_job_code}
                onChange={handleChange}
                className="w-full md:w-1/2 px-4 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all bg-white"
                placeholder="e.g., MBC24001 or OLD-2024-123"
              />
              <p className="text-xs text-amber-600 mt-1">
                Enter the original job ID/reference number from your existing records
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Job Info */}
          <div className="border-b border-slate-200 pb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Job Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Job Title *</label>
                <input
                  data-testid="title-input"
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="e.g., Fix broken HVAC system"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Description *</label>
                <textarea
                  data-testid="description-input"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="Describe the maintenance job..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Job Type *</label>
                <select
                  data-testid="job-type-select"
                  name="job_type"
                  value={formData.job_type}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                >
                  <option value="private">Private</option>
                  <option value="insurance">Insurance</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Priority *</label>
                <select
                  data-testid="priority-select"
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Location *</label>
                <input
                  data-testid="location-input"
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="e.g., Building A, Floor 3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Scheduled Date</label>
                <input
                  data-testid="scheduled-date-input"
                  type="datetime-local"
                  name="scheduled_date"
                  value={formData.scheduled_date}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Estimated Completion Date</label>
                <input
                  data-testid="estimated-completion-date-input"
                  type="datetime-local"
                  name="estimated_completion_date"
                  value={formData.estimated_completion_date}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Estimated Cost (R)</label>
                <input
                  data-testid="estimated-cost-input"
                  type="text"
                  name="estimated_cost"
                  value={formData.estimated_cost_str !== undefined ? formData.estimated_cost_str : formData.estimated_cost}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, '');
                    handleChange({ target: { name: 'estimated_cost', value: value ? parseFloat(value) : 0 } });
                    handleChange({ target: { name: 'estimated_cost_str', value: value } });
                  }}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Client Information */}
          <div className="border-b border-slate-200 pb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Client Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Client Name</label>
                <input
                  data-testid="client-name-input"
                  type="text"
                  name="client_name"
                  value={formData.client_name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Client Email</label>
                <input
                  data-testid="client-email-input"
                  type="email"
                  name="client_email"
                  value={formData.client_email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Client Contact No</label>
                <input
                  data-testid="client-contact-input"
                  type="tel"
                  name="client_contact"
                  value={formData.client_contact}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value);
                    handleChange({ target: { name: 'client_contact', value: formatted } });
                  }}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="+27 21 123 4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Client Alternate Contact</label>
                <input
                  data-testid="client-alt-contact-input"
                  type="tel"
                  name="client_alt_contact"
                  value={formData.client_alt_contact}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value);
                    handleChange({ target: { name: 'client_alt_contact', value: formatted } });
                  }}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="+27 21 123 4567"
                />
              </div>
            </div>
          </div>

          {/* Insurance Information - Conditional */}
          {isInsurance && (
            <div className="border-b border-slate-200 pb-6 bg-blue-50 p-6 rounded-lg">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Insurance Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Insurer Name</label>
                  <input
                    data-testid="insurer-name-input"
                    type="text"
                    name="insurer_name"
                    value={formData.insurer_name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Insurer Contact Person</label>
                  <input
                    data-testid="insurer-contact-person-input"
                    type="text"
                    name="insurer_contact_person"
                    value={formData.insurer_contact_person}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Insurer Email</label>
                  <input
                    data-testid="insurer-email-input"
                    type="email"
                    name="insurer_email"
                    value={formData.insurer_email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Insurer Contact No</label>
                  <input
                    data-testid="insurer-contact-no-input"
                    type="tel"
                    name="insurer_contact_no"
                    value={formData.insurer_contact_no}
                    onChange={(e) => {
                      const formatted = formatPhoneNumber(e.target.value);
                      handleChange({ target: { name: 'insurer_contact_no', value: formatted } });
                    }}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                    placeholder="+27 21 123 4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Insurer Reference No</label>
                  <input
                    data-testid="insurer-ref-input"
                    type="text"
                    name="insurer_reference_no"
                    value={formData.insurer_reference_no}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Insurer Claim No</label>
                  <input
                    data-testid="insurer-claim-input"
                    type="text"
                    name="insurer_claim_no"
                    value={formData.insurer_claim_no}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                  />
                </div>
              </div>

              <h3 className="text-md font-semibold text-slate-900 mt-6 mb-4">Loss Adjuster Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Loss Adjuster Name</label>
                  <input
                    data-testid="loss-adjuster-name-input"
                    type="text"
                    name="loss_adjuster_name"
                    value={formData.loss_adjuster_name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Loss Adjuster Contact</label>
                  <input
                    data-testid="loss-adjuster-contact-input"
                    type="text"
                    name="loss_adjuster_contact"
                    value={formData.loss_adjuster_contact}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Loss Adjuster Contact No</label>
                  <input
                    data-testid="loss-adjuster-contact-no-input"
                    type="tel"
                    name="loss_adjuster_contact_no"
                    value={formData.loss_adjuster_contact_no}
                    onChange={(e) => {
                      const formatted = formatPhoneNumber(e.target.value);
                      handleChange({ target: { name: 'loss_adjuster_contact_no', value: formatted } });
                    }}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                    placeholder="+27 21 123 4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Loss Adjuster Email</label>
                  <input
                    data-testid="loss-adjuster-email-input"
                    type="email"
                    name="loss_adjuster_email"
                    value={formData.loss_adjuster_email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Assignment */}
          <div className="border-b border-slate-200 pb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Assignment</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Assign to Manager/Supervisor *</label>
                <select
                  data-testid="manager-select"
                  name="assigned_manager"
                  value={formData.assigned_manager}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                >
                  <option value="">Select a supervisor</option>
                  {managers.map((manager) => (
                    <option key={manager.id} value={manager.id}>
                      {manager.name} ({manager.role})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-2">
                  Teams and team members can be assigned after the job is created.
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              data-testid="submit-button"
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Job'}
            </button>
            <button
              data-testid="cancel-button"
              type="button"
              onClick={() => navigate('/jobs')}
              className="px-6 py-3 border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateJob;
